import React, { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { ServiceThemeOptions } from '../themes';

interface LogoSectionProps {
  serviceName?: string;
}

interface LogoDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

export const LogoSection: React.FC<LogoSectionProps> = ({ serviceName }) => {
  const theme = useTheme() as ServiceThemeOptions;
  const [targetHeight, setTargetHeight] = useState<number>(40);

  // Get service logo from theme
  const serviceLogo = theme.custom?.serviceLogo;

  // Use the standard Axle logo (dark version for light backgrounds)
  const axleLogo = '/images/logo/axle logo.png';

  // Function to load image and get dimensions
  const loadImageDimensions = (src: string): Promise<LogoDimensions> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
          aspectRatio: img.naturalWidth / img.naturalHeight,
        });
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  // Load both images and calculate balanced sizes
  useEffect(() => {
    // Only run if we have a service logo
    if (!serviceLogo) {
      return;
    }

    const loadBothLogos = async () => {
      try {
        const [serviceDimensions, axleDimensions] = await Promise.all([
          loadImageDimensions(serviceLogo),
          loadImageDimensions(axleLogo),
        ]);

        // Calculate balanced height
        // We want both logos to have similar visual weight
        // Use a base height and adjust based on aspect ratios
        const baseHeight = 40;

        // If one logo is much wider than the other, we might want to adjust
        // For now, let's use a simple approach: use the base height but ensure
        // neither logo gets too wide (max 120px width)
        const serviceWidthAtBaseHeight =
          baseHeight * serviceDimensions.aspectRatio;
        const axleWidthAtBaseHeight = baseHeight * axleDimensions.aspectRatio;

        // If either would be too wide, scale down
        const maxWidth = 120;
        let finalHeight = baseHeight;

        if (serviceWidthAtBaseHeight > maxWidth) {
          finalHeight = Math.min(
            finalHeight,
            maxWidth / serviceDimensions.aspectRatio
          );
        }
        if (axleWidthAtBaseHeight > maxWidth) {
          finalHeight = Math.min(
            finalHeight,
            maxWidth / axleDimensions.aspectRatio
          );
        }

        setTargetHeight(finalHeight);
      } catch (error) {
        console.warn('Failed to load logo dimensions:', error);
        // Fallback to default height
        setTargetHeight(40);
      }
    };

    loadBothLogos();
  }, [serviceLogo, axleLogo]);

  // Only render if theme has a service logo (after all hooks)
  if (!serviceLogo) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        mb: 4,
        width: '100%',
        maxWidth: '449px',
      }}
    >
      {/* Service Logo - Right justified in its space */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <img
          src={serviceLogo}
          alt={serviceName || 'Service'}
          style={{
            height: `${targetHeight}px`,
            maxWidth: '120px',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Plus Sign - Centered */}
      <Typography
        sx={{
          fontSize: '24px',
          fontWeight: 400,
          color: theme.palette?.text?.secondary || '#757575',
          lineHeight: 1,
          px: 1,
        }}
      >
        +
      </Typography>

      {/* Axle Logo - Left justified in its space */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <img
          src={axleLogo}
          alt='Axle'
          style={{
            height: `${targetHeight}px`,
            maxWidth: '120px',
            objectFit: 'contain',
          }}
        />
      </Box>
    </Box>
  );
};
